import { useEffect, useState } from 'react';
import QRCode from 'qrcode';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { api, unwrap } from '@/api/client';
import type { components } from '@/api/schema';
import { PageHeader } from '@/components/layout/PageHeader';
import { Badge, Card, Skeleton } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Field, RootError, Section } from '@/components/ui/form-controls';
import { Input } from '@/components/ui/input';
import { errorMessage } from '@/api/problem';
import { useAuthStore } from '@/auth/authStore';

const meKey = ['global', 'me'] as const;

export function ProfilePage() {
  const { t } = useTranslation();
  const me = useQuery({ queryKey: meKey, queryFn: async () => unwrap(await api.GET('/v1/auth/me')) });

  return (
    <div>
      <PageHeader help="profile" title={t('admin.profile.title')} />
      <div className="flex max-w-2xl flex-col gap-8 p-6">
        {me.isPending && <Skeleton className="h-24 w-full" />}
        {me.data && (
          <>
            <Section title={t('admin.profile.account')}>
              <Card className="grid grid-cols-[8rem_1fr] gap-y-2 p-4 text-sm">
                <span className="text-muted-foreground">{t('admin.users.name')}</span>
                <span>{me.data.displayName}</span>
                <span className="text-muted-foreground">{t('admin.users.email')}</span>
                <span>{me.data.email}</span>
                <span className="text-muted-foreground">{t('admin.users.roles')}</span>
                <span className="flex flex-wrap gap-1">
                  {me.data.roles.map((r) => (
                    <Badge key={r}>{t(`admin.roles.${r}`, { defaultValue: r })}</Badge>
                  ))}
                </span>
                {me.data.caps.length > 0 && (
                  <>
                    <span className="text-muted-foreground">{t('admin.tenantTabs.caps')}</span>
                    <span className="flex flex-wrap gap-1">
                      {me.data.caps.map((c) => (
                        <Badge key={c} variant="accent">
                          {c}
                        </Badge>
                      ))}
                    </span>
                  </>
                )}
              </Card>
            </Section>
            <PasswordSection />
            <TotpSection enabled={me.data.totpEnabled} />
          </>
        )}
      </div>
    </div>
  );
}

function TotpSection({ enabled }: { enabled: boolean }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [enrollment, setEnrollment] = useState<components['schemas']['TotpEnrollResponse'] | null>(null);
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);

  const enroll = useMutation({
    mutationFn: async () => unwrap(await api.POST('/v1/auth/totp/enroll')),
    onSuccess: (r) => setEnrollment(r),
  });
  const confirm = useMutation({
    meta: { silent: true },
    mutationFn: async () => unwrap(await api.POST('/v1/auth/totp/confirm', { body: { code } })),
    onSuccess: async () => {
      setEnrollment(null);
      setCode('');
      setError(null);
      toast.success(t('admin.profile.totpEnabled'));
      await qc.invalidateQueries({ queryKey: meKey });
    },
    onError: (err) => setError(errorMessage(err)),
  });

  return (
    <Section title={t('admin.profile.totp')} description={t('admin.profile.totpHint')}>
      {enabled && !enrollment ? (
        <p className="text-sm">
          <Badge variant="success">{t('admin.profile.totpOn')}</Badge>
        </p>
      ) : enrollment ? (
        <Card className="flex flex-col gap-3 p-4">
          <p className="text-xs text-muted-foreground">{t('admin.profile.totpScan')}</p>
          <TotpQrCode uri={enrollment.otpauthUri} />
          <details className="text-xs">
            <summary className="cursor-pointer text-muted-foreground">{t('admin.profile.totpManual')}</summary>
            <div className="mt-2">
              {t('admin.profile.totpSecret')}: <code className="font-mono">{enrollment.secret}</code>
            </div>
            <code className="mt-2 block break-all rounded bg-muted p-2" data-testid="otpauth">
              {enrollment.otpauthUri}
            </code>
          </details>
          <Field label={t('admin.profile.totpCode')} htmlFor="totp-code">
            <Input id="totp-code" inputMode="numeric" autoComplete="one-time-code" value={code} onChange={(e) => setCode(e.target.value.trim())} className="w-32" />
          </Field>
          <RootError message={error ?? undefined} />
          <div>
            <Button onClick={() => confirm.mutate()} disabled={!/^\d{6}$/.test(code) || confirm.isPending}>
              {t('admin.profile.totpConfirm')}
            </Button>
          </div>
        </Card>
      ) : (
        <div>
          <Button variant="outline" onClick={() => enroll.mutate()} disabled={enroll.isPending}>
            {t('admin.profile.totpEnroll')}
          </Button>
        </div>
      )}
    </Section>
  );
}

/** Rendered in the browser: the TOTP secret never leaves the page (no external QR service). */
function TotpQrCode({ uri }: { uri: string }) {
  const { t } = useTranslation();
  const [svg, setSvg] = useState<string | null>(null);
  useEffect(() => {
    let live = true;
    void QRCode.toString(uri, { type: 'svg', margin: 1, errorCorrectionLevel: 'M' }).then((s) => {
      if (live) setSvg(s);
    });
    return () => {
      live = false;
    };
  }, [uri]);
  if (!svg) return <Skeleton className="h-48 w-48" />;
  return (
    <img
      src={`data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`}
      alt={t('admin.profile.totpQrAlt')}
      data-testid="totp-qr"
      className="h-48 w-48 rounded bg-white p-2"
    />
  );
}

function PasswordSection() {
  const { t } = useTranslation();
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [repeat, setRepeat] = useState('');
  const [error, setError] = useState<string | null>(null);

  const change = useMutation({
    meta: { silent: true },
    mutationFn: async () =>
      unwrap(await api.POST('/v1/auth/password', { body: { currentPassword: current, newPassword: next } })),
    onSuccess: (tokens) => {
      // Every other session was signed out; this browser continues with the new token pair.
      useAuthStore.getState().setSession(tokens);
      setCurrent('');
      setNext('');
      setRepeat('');
      setError(null);
      toast.success(t('admin.profile.passwordChanged'));
    },
    onError: (err) => setError(errorMessage(err)),
  });

  const tooShort = next.length > 0 && next.length < 12;
  const mismatch = repeat.length > 0 && repeat !== next;
  const canSave = current.length > 0 && next.length >= 12 && repeat === next && !change.isPending;

  return (
    <Section title={t('admin.profile.password')} description={t('admin.profile.passwordHint')}>
      <form
        className="flex max-w-sm flex-col gap-3"
        onSubmit={(e) => {
          e.preventDefault();
          if (canSave) change.mutate();
        }}
      >
        <Field label={t('admin.profile.passwordCurrent')} htmlFor="pw-current">
          <Input id="pw-current" type="password" autoComplete="current-password" value={current} onChange={(e) => setCurrent(e.target.value)} />
        </Field>
        <Field label={t('admin.profile.passwordNew')} htmlFor="pw-new" error={tooShort ? t('admin.profile.passwordTooShort') : undefined}>
          <Input id="pw-new" type="password" autoComplete="new-password" value={next} onChange={(e) => setNext(e.target.value)} />
        </Field>
        <Field label={t('admin.profile.passwordRepeat')} htmlFor="pw-repeat" error={mismatch ? t('admin.profile.passwordMismatch') : undefined}>
          <Input id="pw-repeat" type="password" autoComplete="new-password" value={repeat} onChange={(e) => setRepeat(e.target.value)} />
        </Field>
        <RootError message={error ?? undefined} />
        <div>
          <Button type="submit" disabled={!canSave}>
            {t('admin.profile.passwordSave')}
          </Button>
        </div>
      </form>
    </Section>
  );
}
